/*---------------------------------------------------------------
 Project: Ring2All Platform
 Author: Rodrigo Cuadra
 Company: Ring2All LLC
 File: PinListsPage.tsx
 Created: 2025-01-27
 Description:
    Main page for PIN Lists management with list view.
    Displays PIN lists for access control functionality.
----------------------------------------------------------------*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks, Edit, Copy, Trash2, Upload, Download } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useContextStore } from '../../store/context';
import { useDataGrid } from '../../hooks/useDataGrid';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import ImportPinListsModal from './ImportPinListsModal';
import { usePinListList } from './hooks/usePinListList';
import { deletePinListById, navigateToPinListDuplicate } from './pinListsActions';
import { exportPinLists } from './api/pinListsApi';
import { PinList } from './types';

export default function PinListsPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; pinList: PinList | null }>({
        isOpen: false,
        pinList: null
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const { fetchData } = usePinListList({
        domainId: selectedDomain?.id,
    });

    const {
        data,
        loading,
        columns: baseColumns,
        pagination,
        searchQuery,
        sortBy,
        sortOrder,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleColumnToggle,
        refresh
    } = useDataGrid({
        fetchData,
        initialColumns: [
            {
                key: 'name',
                label: t('pinLists:columns.name'),
                visible: true,
                sortable: true
            },
            {
                key: 'description',
                label: t('pinLists:columns.description'),
                visible: true,
                sortable: true
            },
            {
                key: 'membersCount',
                label: t('pinLists:columns.members'),
                visible: true,
                sortable: false
            },
            {
                key: 'enabled',
                label: t('pinLists:columns.enabled'),
                visible: true,
                sortable: true
            },
            { key: 'actions', label: t('pinLists:columns.actions'), visible: true, sortable: false }
        ],
        initialSortBy: 'name',
        initialSortOrder: 'asc',
        initialLimit: 10,
        moduleName: 'pin-lists'
    });

    // Refresh data when domain changes
    useEffect(() => {
        if (selectedDomain?.id !== undefined) {
            refresh();
        }
    }, [selectedDomain?.id]);

    // Add render functions to columns
    const columns = useMemo(() => {
        return baseColumns.map((col) => {
            if (col.key === 'description') {
                return {
                    ...col,
                    render: (_value: unknown, item: PinList) => (
                        <span className="text-sm text-muted-foreground">
                            {item?.description || '-'}
                        </span>
                    )
                };
            }
            if (col.key === 'membersCount') {
                return {
                    ...col,
                    render: (_value: unknown, item: PinList) => (
                        <span className="text-sm">
                            {item?._count?.members ?? item?.members?.length ?? 0}
                        </span>
                    )
                };
            }
            if (col.key === 'enabled') {
                return {
                    ...col,
                    render: (_value: unknown, item: PinList) => (
                        <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                }`}
                        >
                            {item?.enabled ? t('common:enabled') : t('common:disabled')}
                        </span>
                    )
                };
            }
            return col;
        });
    }, [baseColumns, t]);

    const handleAdd = useCallback(() => {
        navigate('/pbx/applications/pin-list/new');
    }, [navigate]);

    const handleEdit = useCallback(
        (pinList: PinList) => {
            navigate(`/pbx/applications/pin-list/${pinList.uuid || pinList.id}`);
        },
        [navigate]
    );

    const handleDuplicate = useCallback(
        (pinList: PinList) => {
            navigateToPinListDuplicate(navigate, String(pinList.uuid || pinList.id));
        },
        [navigate]
    );

    const handleDeleteClick = useCallback((pinList: PinList) => {
        setDeleteModal({ isOpen: true, pinList });
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteModal.pinList) return;

        setIsDeleting(true);
        try {
            await deletePinListById(String(deleteModal.pinList.uuid || deleteModal.pinList.id));

            showSuccess(t('pinLists:messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, pinList: null });
            refresh();
        } catch (error) {
            showError(error instanceof Error ? error.message : t('pinLists:messages.deleteError'));
        } finally {
            setIsDeleting(false);
        }
    }, [deleteModal.pinList, refresh, showError, showSuccess, t]);

    const handleImportSuccess = useCallback(() => {
        setImportModalOpen(false);
        refresh();
        showSuccess(t('pinLists:messages.importSuccess'));
    }, [refresh, showSuccess, t]);

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
            const blob = await exportPinLists();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pin-lists-export-${new Date().toISOString().split('T')[0]}.csv`);
            link.setAttribute('style', 'display: none');

            document.body.appendChild(link);
            requestAnimationFrame(() => {
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }, 100);
            });

            showSuccess(t('pinLists:messages.exportSuccess'));
        } catch (error) {
            console.error('Error exporting PIN lists:', error);
            showError(t('pinLists:messages.exportError'));
        } finally {
            setIsExporting(false);
        }
    }, [showError, showSuccess, t]);

    const renderRowActions = useCallback(
        (item: PinList) => (
            <div className="flex items-center gap-2">
                <SimpleTooltip content={t('pinLists:actions.edit')} position="top">
                    <button
                        onClick={() => handleEdit(item)}
                        className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                    >
                        <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </button>
                </SimpleTooltip>
                <SimpleTooltip content={t('pinLists:actions.duplicate')} position="top">
                    <button
                        onClick={() => handleDuplicate(item)}
                        className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                    >
                        <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    </button>
                </SimpleTooltip>
                <SimpleTooltip content={t('pinLists:actions.delete')} position="top">
                    <button
                        onClick={() => handleDeleteClick(item)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                    >
                        <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </button>
                </SimpleTooltip>
            </div>
        ),
        [handleDeleteClick, handleDuplicate, handleEdit, t]
    );

    const extraActions = useMemo(() => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('pinLists:actions.export')} position="top">
                <button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('pinLists:actions.export')}</span>
                </button>
            </SimpleTooltip>
            <SimpleTooltip content={t('pinLists:actions.import')} position="top">
                <button
                    onClick={() => setImportModalOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                    <Upload className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('pinLists:actions.import')}</span>
                </button>
            </SimpleTooltip>
        </div>
    ), [t, isExporting, handleExport]);

    return (
        <ModulePageContainer
            currentView="list"
            listView={
                <DataGridSimple
                    data={data}
                    columns={columns}
                    loading={loading}
                    pagination={pagination}
                    searchQuery={searchQuery}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    onColumnToggle={handleColumnToggle}
                    onAdd={handleAdd}
                    title={t('pinLists:title')}
                    titleIcon={<ListChecks className="h-5 w-5 text-primary" />}
                    addButtonText={t('pinLists:actions.add')}
                    renderActions={renderRowActions}
                    searchPlaceholder={t('pinLists:searchPlaceholder')}
                    emptyMessage={t('pinLists:emptyMessage')}
                    actions={extraActions}
                />
            }
            formView={null}
        >
            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, pinList: null })}
                onConfirm={handleDeleteConfirm}
                title={t('pinLists:messages.deleteTitle')}
                message={t('pinLists:messages.confirmDelete', { name: deleteModal.pinList?.name || '' })}
                confirmText={t('pinLists:actions.delete')}
                cancelText={t('pinLists:actions.cancel')}
                type="danger"
                isLoading={isDeleting}
            />
            <ImportPinListsModal
                isOpen={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                onSuccess={handleImportSuccess}
            />
        </ModulePageContainer>
    );
}
